import React, { FC, FormEventHandler, useCallback, useState } from 'react';
// UI
import { Button, CloseIcon, InputField, Text, View } from '@upstox/upstox-ui';
// modules
import LocaleService from 'modules/Localization';
import { useStore } from 'modules/Stores';
import { PositionsCalculationService } from 'modules/TradesPW3/Positions/helpers/positionsCalculation';
// styles
import * as css from './styles';
// typings
import { Props } from './typings';

const ConvertPositionModal: FC<Props> = props => {
  const { position, positionId } = props;
  const { product } = position;
  const [quantity, setQuantity] = useState('0');
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { toggleModal } = useStore('AppStore');
  const { getLoading } = useStore('LoadingStore');
  const {
    setConvertingPositionId,
    startPositionConversion,
    validateUserInput,
  } = useStore('PositionsPW3Service');
  const convertTextKey =
    product === 'I' ? 'CONVERT_INTRADAY_POSITION' : 'CONVERT_DELIVERY_POSITION';
  const convertText = LocaleService.translate(convertTextKey);
  const netQuantity = PositionsCalculationService.getNetQty(position);
  const isDisabled = error;
  const isLoading = getLoading('CONVERT_POSITION');

  const closeModal = useCallback(() => {
    toggleModal('convertPosition');
    setConvertingPositionId('');
  }, [setConvertingPositionId, toggleModal]);

  const handleQuantityChange: FormEventHandler<HTMLInputElement> = useCallback(
    async event => {
      const { value } = event.target as HTMLInputElement;
      const { message, status } = await validateUserInput(value, positionId);
      setQuantity(value);
      setError(!status);
      setErrorMessage(message);
    },
    [positionId, validateUserInput],
  );

  const handleCancelClick = useCallback(() => {
    closeModal();
  }, [closeModal]);

  const handleAction = useCallback(async () => {
    const { status, message } = await validateUserInput(quantity, positionId);

    if (status) {
      startPositionConversion(positionId, quantity);
      closeModal();
    } else {
      setError(!status);
      setErrorMessage(message);
    }
  }, [
    closeModal,
    positionId,
    quantity,
    startPositionConversion,
    validateUserInput,
  ]);

  return (
    <View css={css.modalContainer}>
      <View css={css.modal}>
        <View css={css.base}>
          <View css={css.header}>
            <Text css={css.title}>
              {LocaleService.translate('CONVERT_POSITION')}
            </Text>
            <CloseIcon css={css.closeIcon} onClick={closeModal} />
          </View>
          <View css={css.modalBody}>
            <View css={css.modalContent} flexDirection="column">
              <View>
                <Text css={css.convertText}>{convertText}</Text>
              </View>
              <View css={css.inputFieldsContainer}>
                <View css={css.inputContainer}>
                  <Text css={css.inputLabel}>
                    {LocaleService.translate('TOTAL_QUANTITY')}
                  </Text>
                  <InputField
                    type="number"
                    value={netQuantity.toString()}
                    disabled
                    css={css.inputField}
                  />
                </View>
                <View css={css.inputContainer}>
                  <Text css={css.inputLabel}>
                    {LocaleService.translate('QUANTITY_TO_CONVERT')}
                  </Text>
                  <InputField
                    type="number"
                    value={quantity}
                    onChange={handleQuantityChange}
                    InputProps={{
                      min: 0,
                      max: netQuantity,
                    }}
                    css={css.inputField}
                    {...(error && { error: errorMessage })}
                  />
                </View>
              </View>
            </View>
            <View css={css.modalFooter}>
              <View css={css.buttonsContainer}>
                <Button css={css.button} onClick={handleCancelClick}>
                  {LocaleService.translate('CANCEL_BUTTON')}
                </Button>
                <Button
                  cssProps={{ disabled: isDisabled }}
                  css={css.convertButton}
                  onClick={handleAction}
                  isLoading={isLoading}
                  disabled={isDisabled}
                >
                  {LocaleService.translate('CONVERT')}
                </Button>
              </View>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

export default ConvertPositionModal;
